import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, of, map, Observable } from 'rxjs';
import {
  NetworkService,
  Network,
  NetworkBodyShop,
} from '../../../core/services/network.service';
import { UserSessionService } from '../../../core/services/mock-auth.service';
import { FormDropdownService } from '../../../core/services/form-dropdown.service';
import { GeographyService } from '../../../core/services/geography.service';

@Component({
  selector: 'app-network-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    TableModule,
    DialogModule,
    SelectModule,
    CheckboxModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <div class="network-detail-container p-4">
      <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true" />
      <p-confirmDialog role="alertdialog" aria-modal="true" />

      <!-- Header -->
      <div class="page-header mb-4">
        <button
          pButton
          icon="pi pi-arrow-left"
          label="Torna al Registro"
          class="p-button-text mb-3"
          (click)="backToRegistry()"
        ></button>
        <h1 class="text-3xl font-bold text-primary">Dettaglio Network di Carrozzerie</h1>
      </div>

      @if (network()) {
        <!-- Card Informazioni Network -->
        <p-card styleClass="mb-4">
          <ng-template pTemplate="header">
            <div class="card-header-custom p-3">
              <h2 class="text-xl font-semibold m-0">
                <i class="pi pi-building mr-2" aria-hidden="true"></i>
                {{ network()!.networkName }}
              </h2>
            </div>
          </ng-template>

          <div class="grid">
            <div class="col-12 md:col-6">
              <div class="field-display mb-3">
                <label class="font-semibold text-gray-700">ID Professional:</label>
                <p class="mt-1">{{ network()!.id }}</p>
              </div>
              <div class="field-display mb-3">
                <label class="font-semibold text-gray-700">Nome Network:</label>
                <p class="mt-1">{{ network()!.networkName }}</p>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="field-display mb-3">
                <label class="font-semibold text-gray-700">Data Creazione:</label>
                <p class="mt-1">{{ network()!.createdAt | date: 'dd/MM/yyyy HH:mm' }}</p>
              </div>
              <div class="field-display mb-3">
                <label class="font-semibold text-gray-700">Ultimo Aggiornamento:</label>
                <p class="mt-1">{{ network()!.updatedAt | date: 'dd/MM/yyyy HH:mm' }}</p>
              </div>
            </div>
            <div class="col-12">
              <div class="field-display">
                <label class="font-semibold text-gray-700">Descrizione:</label>
                <p class="mt-1">{{ network()!.description || 'Nessuna descrizione' }}</p>
              </div>
            </div>
          </div>

          <ng-template pTemplate="footer">
            @if (isAxaUser()) {
              <div class="flex gap-2">
                <button
                  pButton
                  label="Modifica Network"
                  icon="pi pi-pencil"
                  class="p-button-outlined"
                  (click)="editNetwork()"
                ></button>
                <button
                  pButton
                  label="Elimina Network"
                  icon="pi pi-trash"
                  severity="danger"
                  class="p-button-outlined"
                  (click)="confirmDeleteNetwork()"
                ></button>
              </div>
            }
          </ng-template>
        </p-card>

        <!-- Pulsante Aggiungi Carrozzeria (solo per AXA) -->
        @if (isAxaUser()) {
          <div class="mb-4">
            <button
              pButton
              label="Aggiungi Carrozzeria nel Network"
              icon="pi pi-plus"
              class="p-button-success"
              (click)="showAddBodyShopDialog()"
            ></button>
          </div>
        }

        <!-- Warning se manca controllante -->
        @if (!hasControllante()) {
          <div class="warning-banner mb-4">
            <i class="pi pi-exclamation-triangle"></i>
            <div class="warning-content">
              <strong>Attenzione!</strong>
              <p>
                Questo network non ha una carrozzeria controllante. È necessario assegnarne una
                per garantire il corretto funzionamento.
              </p>
              @if (bodyShops().length > 0 && isAxaUser()) {
                <button
                  pButton
                  label="Assegna Controllante"
                  icon="pi pi-star"
                  size="small"
                  (click)="showAssignControllante()"
                ></button>
              }
            </div>
          </div>
        }

        <!-- Tabella Carrozzerie del Network -->
        <p-card>
          <ng-template pTemplate="header">
            <div class="card-header-custom p-3">
              <h2 class="text-xl font-semibold m-0">
                <i class="pi pi-list mr-2" aria-hidden="true"></i>
                Carrozzerie del Network ({{ bodyShops().length }})
              </h2>
            </div>
          </ng-template>

          @if (bodyShops().length === 0) {
            <div class="empty-state text-center py-5">
              <i class="pi pi-inbox text-6xl text-gray-400 mb-3" aria-hidden="true"></i>
              <p class="text-gray-600 text-lg">Nessuna carrozzeria associata a questo network</p>
              @if (isAxaUser()) {
                <button
                  pButton
                  label="Aggiungi Prima Carrozzeria"
                  icon="pi pi-plus"
                  (click)="showAddBodyShopDialog()"
                ></button>
              }
            </div>
          } @else {
            <p-table [value]="bodyShops()" [tableStyle]="{ 'min-width': '60rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>ID</th>
                  <th>Codice SOL</th>
                  <th>Nominativo</th>
                  <th>Indirizzo</th>
                  <th>Data aggiunta</th>
                  <th>Data cessazione</th>
                  <th>Stato</th>
                  @if (isAxaUser()) {
                    <th style="width: 200px">Azioni</th>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-bs>
                <tr>
                  <td>{{ bs.id }}</td>
                  <td>{{ bs.solCode || '-' }}</td>
                  <td>{{ bs.nominativo || bs.businessName || bs.id }}</td>
                  <td>{{ bs.indirizzoCompatto || '-' }}</td>
                  <td>{{ bs.addedToNetworkAt ? (bs.addedToNetworkAt | date: 'dd/MM/yyyy') : '-' }}</td>
                  <td>{{ bs.cessationDate ? (bs.cessationDate | date: 'dd/MM/yyyy') : '-' }}</td>
                  <td>
                    <span
                      class="status-badge"
                      [class.status-active]="bs.status === 'ACTIVE'"
                      [class.status-pending]="bs.status === 'IN_PROGRESS'"
                      [class.status-suspended]="bs.status === 'SUSPENDED'"
                      [class.status-ceased]="bs.status === 'CEASED'"
                    >
                      {{ bs.status }}
                    </span>
                  </td>
                  @if (isAxaUser()) {
                    <td>
                      <div class="flex gap-2">
                        <button
                          pButton
                          icon="pi pi-eye"
                          class="p-button-rounded p-button-text"
                          pTooltip="Visualizza Dettaglio"
                          (click)="viewBodyShop(bs.id)"
                          [attr.aria-label]="'Visualizza Dettaglio carrozzeria ' + (bs.nominativo || bs.businessName || bs.id)"
                        ></button>
                        @if (bs.status === 'CEASED') {
                          <button
                            pButton
                            icon="pi pi-refresh"
                            class="p-button-rounded p-button-text p-button-success"
                            pTooltip="Riattiva nel Network"
                            (click)="reactivateBodyShop(bs.id, bs.nominativo || bs.businessName || bs.id)"
                            [attr.aria-label]="'Riattiva nel network la carrozzeria ' + (bs.nominativo || bs.businessName || bs.id)"
                          ></button>
                        } @else {
                          <button
                            pButton
                            icon="pi pi-trash"
                            class="p-button-rounded p-button-text p-button-danger"
                            pTooltip="Rimuovi dal Network"
                            (click)="confirmRemoveBodyShop(bs.id)"
                            [attr.aria-label]="'Rimuovi dal Network la carrozzeria ' + (bs.nominativo || bs.businessName || bs.id)"
                          ></button>
                        }
                      </div>
                    </td>
                  }
                </tr>
              </ng-template>
            </p-table>
          }
        </p-card>
      } @else {
        <div class="loading-state text-center py-5">
          <i class="pi pi-spin pi-spinner text-4xl text-primary" aria-hidden="true"></i>
          <p class="mt-3">Caricamento network...</p>
        </div>
      }

      <!-- Dialog Aggiungi Carrozzeria -->
      <p-dialog
        [(visible)]="showAddDialog"
        header="Aggiungi Carrozzeria al Network"
        [modal]="true"
        [style]="{ width: '500px' }"
      >
        <div class="dialog-content">
          <div class="field">
            <label for="bodyshop-select" class="font-semibold block mb-2"
              >Seleziona Carrozzeria</label
            >
            <p-select
              inputId="bodyshop-select"
              [options]="availableBodyShops"
              [(ngModel)]="selectedBodyShop"
              optionLabel="businessName"
              optionValue="id"
              placeholder="Seleziona una carrozzeria"
              [style]="{ width: '100%' }"
            ></p-select>
          </div>

          <div class="field-checkbox mt-3">
            <p-checkbox
              inputId="set-controllante"
              [(ngModel)]="setAsControllante"
              [binary]="true"
            />
            <label for="set-controllante" class="ml-2">Imposta come controllante</label>
          </div>

          @if (setAsControllante && hasControllante()) {
            <div class="warning-message mt-2">
              <i class="pi pi-exclamation-triangle mr-2"></i>
              <small
                >Impostando questa carrozzeria come controllante, la controllante attuale verrà
                declassata ad affiliata.</small
              >
            </div>
          }
        </div>

        <ng-template pTemplate="footer">
          <button pButton label="Annulla" class="p-button-text" (click)="closeAddDialog()"></button>
          <button
            pButton
            label="Aggiungi"
            (click)="addBodyShop()"
            [disabled]="!selectedBodyShop"
          ></button>
        </ng-template>
      </p-dialog>
    </div>
  `,
  styles: [
    `
      .network-detail-container {
        max-width: 1400px;
        margin: 0 auto;
      }

      .page-header {
        h1 {
          color: #00125b;
        }
      }

      .card-header-custom {
        border-bottom: 1px solid #e0e0e0;

        h2 {
          color: #00125b;
        }
      }

      .field-display {
        label {
          display: block;
          font-size: 0.875rem;
        }

        p {
          font-size: 1rem;
          color: #5b6370;
        }
      }

      .warning-banner {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        padding: 1rem 1.5rem;
        background: #fff3cd;
        border-left: 4px solid #ffc107;
        border-radius: 6px;

        i {
          color: #ff9800;
          font-size: 1.5rem;
          margin-top: 0.25rem;
        }

        .warning-content {
          flex: 1;

          strong {
            color: #f57c00;
            display: block;
            margin-bottom: 0.25rem;
          }

          p {
            margin: 0 0 0.75rem 0;
            color: #5b6370;
          }
        }
      }

      .badge-controllante {
        display: inline-flex;
        align-items: center;
        padding: 0.25rem 0.75rem;
        background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
        color: #795548;
        border-radius: 20px;
        font-weight: 600;
        font-size: 0.875rem;
        box-shadow: 0 2px 4px rgba(255, 215, 0, 0.3);
      }

      .badge-affiliata {
        display: inline-flex;
        align-items: center;
        padding: 0.25rem 0.75rem;
        background: #e0e0e0;
        color: #424242;
        border-radius: 20px;
        font-weight: 500;
        font-size: 0.875rem;
      }

      .status-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.875rem;
        font-weight: 500;

        &.status-active {
          background: #c8e6c9;
          color: #2e7d32;
        }

        &.status-pending {
          background: #fff9c4;
          color: #f57f17;
        }
      }

      .completion-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.875rem;
        font-weight: 500;

        &.completed {
          background: #c8e6c9;
          color: #2e7d32;
        }

        &.pending {
          background: #ffccbc;
          color: #d84315;
        }
      }

      .empty-state {
        i {
          display: block;
        }
      }

      .warning-message {
        display: flex;
        align-items: center;
        padding: 0.75rem;
        background: #fff3cd;
        border-radius: 4px;

        i {
          color: #ff9800;
        }

        small {
          color: #795548;
        }
      }
    `,
  ],
})
export class NetworkDetailComponent implements OnInit {
  private networkService = inject(NetworkService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private mockAuthService = inject(UserSessionService);
  private titleService = inject(Title);
  private formDropdownService = inject(FormDropdownService);
  private geographyService = inject(GeographyService);

  network = signal<Network | null>(null);
  bodyShops = signal<NetworkBodyShop[]>([]);
  controllante = signal<NetworkBodyShop | null>(null);
  hasControllante = computed(() => !!this.controllante());

  // Verifica se l'utente è AXA (mock - da sostituire con vera autenticazione)
  isAxaUser = signal<boolean>(true);

  // Dialog
  showAddDialog = false;
  selectedBodyShop: string | null = null;
  setAsControllante = false;
  availableBodyShops: any[] = []; // Mock - da caricare da API

  ngOnInit() {
    this.titleService.setTitle('Dettaglio Network - PalEasy');
    
    const networkId = this.route.snapshot.params['id'];
    if (networkId) {
      this.loadNetwork(networkId);
      this.loadBodyShops(networkId);
      this.loadControllante(networkId);
    }
  }

  loadNetwork(id: string) {
    this.networkService.getNetwork(id).subscribe({
      next: (res) => {
        if (res.status) {
          this.network.set(res.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Network non trovato',
          });
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare il network',
        });
      },
    });
  }

  loadBodyShops(id: string) {
    forkJoin({
      bodyShops: this.networkService.getNetworkBodyShops(id),
      streetTypes: this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME'),
    }).subscribe({
      next: ({ bodyShops, streetTypes }) => {
        if (bodyShops.status) {
          const streetTypeMap = new Map<string, string>(
            (streetTypes?.data ?? []).map((item: any) => [item.valueKey, item.valueDescription])
          );

          const items = Array.isArray(bodyShops.data) ? bodyShops.data : [];
          const enriched$ = items.map((bs) => {
            const nominativo = bs.businessName
              || [bs.firstname, bs.lastname].filter(Boolean).join(' ')
              || bs.id;
            const addr = bs.facilityAddress ?? bs.legalAddress ?? null;
            return this.formatCompactAddress(addr, streetTypeMap).pipe(
              map((indirizzoCompatto) => ({ ...bs, nominativo, indirizzoCompatto })),
            );
          });
          (enriched$.length ? forkJoin(enriched$) : of([])).subscribe((enriched) => {
            this.bodyShops.set(enriched);
          });
        } else {
          this.bodyShops.set([]);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le carrozzerie',
        });
        this.bodyShops.set([]);
      },
    });
  }

  /**
   * Formatta un indirizzo compatto per la sola visualizzazione, decodificando il
   * tipo via (mappa PLACE_NAME) e il nome del comune a partire dal codice salvato.
   * Restituisce un Observable perché la decodifica del comune richiede l'anagrafica
   * (caricata per provincia e messa in cache dal GeographyService); i dati legacy
   * salvati come nome vengono comunque mostrati grazie al fallback in resolveMunicipalityName.
   */
  private formatCompactAddress(addr: any, streetTypeMap: Map<string, string>): Observable<string> {
    if (!addr) return of('');
    return this.geographyService
      .resolveMunicipalityName(addr.municipality, addr.province)
      .pipe(
        map((municipality) =>
          [
            [streetTypeMap.get(addr.streetType || '') || addr.streetType, addr.streetName, addr.streetNumber]
              .filter(Boolean)
              .join(' '),
            [municipality, addr.province ? `(${addr.province})` : null].filter(Boolean).join(' '),
            addr.zipCode,
          ]
            .filter(Boolean)
            .join(', '),
        ),
      );
  }

  loadControllante(id: string) {
    this.networkService.getControllante(id).subscribe({
      next: (res) => {
        if (res.status) {
          this.controllante.set(res.data);
        }
      },
      error: () => {
        // Controllante non trovata - non mostrare errore
        this.controllante.set(null);
      },
    });
  }

  showAddBodyShopDialog() {
    // TODO: Caricare lista carrozzerie disponibili da API
    this.availableBodyShops = [];
    this.selectedBodyShop = null;
    this.setAsControllante = false;
    this.showAddDialog = true;
  }

  closeAddDialog() {
    this.showAddDialog = false;
    this.selectedBodyShop = null;
    this.setAsControllante = false;
  }

  addBodyShop() {
    if (!this.selectedBodyShop || !this.network()) return;

    const shop = this.availableBodyShops.find((s: any) => s.id === this.selectedBodyShop);
    const businessName: string = shop?.businessName ?? this.selectedBodyShop ?? '';

    this.networkService
      .addBodyShopToNetwork(this.network()!.id, { businessName, controllante: this.setAsControllante })
      .subscribe({
        next: (res) => {
          if (res.status) {
            this.messageService.add({
              severity: 'success',
              summary: 'Successo',
              detail: 'Carrozzeria aggiunta al network',
            });
            this.loadBodyShops(this.network()!.id);
            this.loadControllante(this.network()!.id);
            this.closeAddDialog();
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile aggiungere la carrozzeria',
          });
        },
      });
  }

  promoteToControllante(bodyShopId: string) {
    if (!this.network()) return;

    this.confirmationService.confirm({
      message: 'Vuoi promuovere questa carrozzeria a controllante del network?',
      header: 'Conferma Promozione',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const shop = this.bodyShops().find(s => s.id === bodyShopId);
        const businessName: string = shop?.businessName ?? shop?.nominativo ?? bodyShopId;

        this.networkService
          .addBodyShopToNetwork(this.network()!.id, { businessName, controllante: true })
          .subscribe({
            next: (res) => {
              if (res.status) {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Successo',
                  detail: 'Carrozzeria promossa a controllante',
                });
                this.loadBodyShops(this.network()!.id);
                this.loadControllante(this.network()!.id);
              }
            },
            error: () => {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: 'Impossibile promuovere la carrozzeria',
              });
            },
          });
      },
    });
  }

  confirmRemoveBodyShop(bodyShopId: string) {
    this.confirmationService.confirm({
      message: 'Vuoi rimuovere questa carrozzeria dal network?',
      header: 'Conferma Rimozione',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.networkService.removeBodyShopFromNetwork(bodyShopId).subscribe({
          next: (res) => {
            if (res.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Successo',
                detail: 'Carrozzeria rimossa dal network',
              });
              this.loadBodyShops(this.network()!.id);
              this.loadControllante(this.network()!.id);
            }
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile rimuovere la carrozzeria',
            });
          },
        });
      },
    });
  }

  reactivateBodyShop(bodyShopId: string, displayName: string) {
    if (!this.network()) return;

    this.confirmationService.confirm({
      message: `Vuoi riattivare la carrozzeria <strong>${displayName}</strong> nel network?`,
      header: 'Conferma Riattivazione',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Riattiva',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.networkService.updateBodyShopStatus(bodyShopId, 'REACTIVATE').subscribe({
          next: (res) => {
            if (res.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Successo',
                detail: 'Carrozzeria riattivata nel network',
              });
              this.loadBodyShops(this.network()!.id);
              this.loadControllante(this.network()!.id);
            }
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile riattivare la carrozzeria',
            });
          },
        });
      },
    });
  }

  showAssignControllante() {
    this.showAddBodyShopDialog();
    this.setAsControllante = true;
  }

  viewBodyShop(bodyShopId: string) {
    this.router.navigate(['/axa/bodyshop-detail', bodyShopId]);
  }

  editNetwork() {
    // TODO: Implementare dialog o navigazione per modifica network
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Funzionalità modifica network in sviluppo',
    });
  }

  confirmDeleteNetwork() {
    // TODO: Implementare eliminazione network tramite apposita API
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Funzionalità eliminazione network in sviluppo',
    });
  }

  backToRegistry() {
    this.router.navigate(['/axa/professionals-registry']);
  }
}
